import { getMarginType } from "../utils/utilfunction";
const { extractSymbolForMargin } = require('./useOrderFormUtils');

const isCashEquityExchange = (exchange) => exchange === "NSE" || exchange === "BSE";
const isEquityInstrument = (instrumentName) => instrumentName === "EQ" || instrumentName === "E";
const isFuturesInstrument = (instrumentName = "") =>
  instrumentName.startsWith("FUT") || instrumentName === "IF" || instrumentName === "SF";
const isOptionsInstrument = (instrumentName = "") =>
  instrumentName.startsWith("OPT") || instrumentName === "IO" || instrumentName === "SO";

// SIMPLIFIED: Only match Symbol + Product Type + Time Range
export const getMarginMultiplierFromConfig = ({
  marginConfig,
  exchange,
  symbol,
  productType,
  instrumentName = "",
  currentTime = new Date(),
  instrumentDetails = null,
}) => {
  if (!marginConfig || !marginConfig[exchange]) {
    return null;
  }

  // Get current time in HH:MM format
  const currentTimeStr = currentTime.toTimeString().substring(0, 5);

  // Get current day name and filter configs by day
  const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
  const exchangeConfigs = marginConfig[exchange];
  const todayConfigs = exchangeConfigs[currentDay] || [];

  // SIMPLE MATCHING: Only Symbol + Product Type + Time Range + Day
  for (let i = 0; i < todayConfigs.length; i++) {
    const config = todayConfigs[i];

    // 1. Check symbol match
    if (config.Symbol && Array.isArray(config.Symbol) && config.Symbol.includes(symbol)) {
      // 2. Additional validation for FUTCOM instruments
      if (instrumentDetails) {
        const isValidFutcom = instrumentDetails.instrument_name === "FUTCOM";
        const symbolInInstrumentDetails = config.Symbol.some(
          (configSymbol) =>
            instrumentDetails.trading_symbol?.includes(configSymbol) ||
            instrumentDetails.display_name?.includes(configSymbol)
        );

        if (!isValidFutcom || !symbolInInstrumentDetails) {
          continue;
        }
      }

      // 3. Check product type match
      if (config.ProductType === productType) {
        // 4. Check time range
        if (config.StartTime && config.EndTime) {
          const timeInRange =
            currentTimeStr >= config.StartTime && currentTimeStr <= config.EndTime;

          if (timeInRange && config.IsActive) {
            if (config.MultiplierPerc && config.MultiplierPerc > 0 && isFinite(config.MultiplierPerc)) {
              return config.MultiplierPerc;
            } else {
              return null;
            }
          }
        }
      }
    }
  }

  return null;
};

export const calcMargin = ({
  order_config,
  exchange,
  orderSide,
  product,
  price,
  qty,
  multiplier,
  strike_price,
  instrumentName = "",
  raw_board_lot_quantity = 1,
  marginConfig = null,
  symbol = "",
  instrumentDetails = null,
}) => {
  // Import extractSymbolForMargin for symbol cleaning
  // Clean the symbol at the start of the function
  const cleanedSymbol = extractSymbolForMargin(exchange, symbol, instrumentDetails);

  // STEP 1: Check for dynamic margin config first (Wednesday/Thursday case)
  if (marginConfig && cleanedSymbol) {
    const dynamicMultiplier = getMarginMultiplierFromConfig({
      marginConfig,
      exchange,
      symbol: cleanedSymbol,
      productType: product,
      instrumentName,
      instrumentDetails: instrumentDetails,
    });
    if (dynamicMultiplier !== null) {
      // Use dynamic multiplier (percentage based)
      if (dynamicMultiplier && dynamicMultiplier > 0 && isFinite(dynamicMultiplier)) {
        let marginValue;

        marginValue = (Number(price) * qty) / dynamicMultiplier;

        if (isFinite(marginValue)) {
          return marginValue;
        }
      }
    }
  }

  // STEP 2: Fallback to static margin calculation
  const marginType = getMarginType(product);
  let marginVal = 0;
  const exchangeConfig = order_config[exchange];

  // Handle option sell orders consistently for all exchanges including MCX
  if (orderSide === "SELL" && isOptionsInstrument(instrumentName)) {
    let marginMultiplier;

    // Get the margin multiplier based on exchange
    if (isCashEquityExchange(exchange)) {
      marginMultiplier = exchangeConfig[orderSide][marginType.toLowerCase()];
      marginVal = marginMultiplier.multiplier;
    } else {
      // NFO, BFO, MCX, or other derivative exchanges
      marginMultiplier =
        exchangeConfig["OPTIONS"][orderSide][marginType.toLowerCase()];

      const tradingUnitFactor = (exchange === "MCX" && instrumentDetails?.instrument_name === "OPTFUT") ? parseFloat(instrumentDetails?.trading_unit_factor || 0) : 0;
      marginVal =
        marginMultiplier.absolute === 0
          ? marginMultiplier.multiplier
          : tradingUnitFactor > 0
            ? marginMultiplier.absolute / (raw_board_lot_quantity * tradingUnitFactor)
            : marginMultiplier.absolute / raw_board_lot_quantity;
    }

    const result = qty * marginVal;
    return result;
  }

  // Handle all other order types with existing logic
  if (isFuturesInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    const marginMultiplier =
      exchangeConfig["FUTURES"][orderSide][marginType.toLowerCase()];

    marginVal =
      marginMultiplier.absolute === 0
        ? marginMultiplier.multiplier
        : marginMultiplier.absolute;
  } else if (isOptionsInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    const marginMultiplier =
      exchangeConfig["OPTIONS"][orderSide][marginType.toLowerCase()];
    const tradingUnitFactor = (exchange === "MCX" && instrumentDetails?.instrument_name === "OPTFUT") ? parseFloat(instrumentDetails?.trading_unit_factor || 0) : 0;
    marginVal =
      marginMultiplier.absolute === 0
        ? marginMultiplier.multiplier
        : tradingUnitFactor > 0
          ? marginMultiplier.absolute / (raw_board_lot_quantity * tradingUnitFactor)
          : marginMultiplier.absolute / raw_board_lot_quantity;
  } else if (isFuturesInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    const marginMultiplier = exchangeConfig[marginType?.toLowerCase()];
    marginVal =
      marginMultiplier.absolute === 0
        ? marginMultiplier.multiplier
        : marginMultiplier.absolute;
  } else if (isOptionsInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    const marginMultiplier =
      exchangeConfig["OPTIONS"][orderSide][marginType.toLowerCase()];
    marginVal =
      marginMultiplier.absolute === 0
        ? marginMultiplier.multiplier
        : marginMultiplier.absolute / raw_board_lot_quantity;
  }
  else if(isEquityInstrument(instrumentName) && exchange === "CDS"){
    const marginMultiplier = exchangeConfig["EQUITY"][orderSide][marginType.toLowerCase()];
    marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute / raw_board_lot_quantity;
  }
  else if(exchange === "DCSX") {
    const marginMultiplier = exchangeConfig["CRYPTO"][orderSide][marginType.toLowerCase()];
    marginVal = marginMultiplier.absolute === 0 ? marginMultiplier.multiplier : marginMultiplier.absolute / raw_board_lot_quantity;
  }

  if (isCashEquityExchange(exchange) && isEquityInstrument(instrumentName)) {
    // if (orderSide === "SELL") {
    //   return Number(price) * qty;

    // } else {

    if (exchangeConfig["EQUITY"] && exchangeConfig["EQUITY"][orderSide]) {
      const eqCfg = exchangeConfig["EQUITY"][orderSide][marginType?.toLowerCase()];
      if (eqCfg) {
        marginVal = eqCfg.absolute === 0 ? eqCfg.multiplier : eqCfg.absolute;
      }
    }
    if (!marginVal || marginVal === 0) {
      const topCfg = exchangeConfig[marginType?.toLowerCase()];
      if (topCfg) {
        marginVal = topCfg.absolute === 0 ? topCfg.multiplier : topCfg.absolute;
      }
    }

  }

  if (!marginVal || marginVal === 0) {
    try {
      if (exchangeConfig["EQUITY"] && exchangeConfig["EQUITY"][orderSide]) {
        const eqCfg = exchangeConfig["EQUITY"][orderSide][marginType?.toLowerCase()];
        if (eqCfg) {
          marginVal = eqCfg.absolute === 0 ? eqCfg.multiplier : eqCfg.absolute / raw_board_lot_quantity;
        }
      }
      if ((!marginVal || marginVal === 0) && isCashEquityExchange(exchange)) {
        const topCfg = exchangeConfig[marginType?.toLowerCase()];
        if (topCfg) {
          marginVal = topCfg.absolute === 0 ? topCfg.multiplier : topCfg.absolute;
        }
      }
    } catch (e) {
      console.warn("calcMargin: error while applying equity fallback", e);
    }
  }

  // For non-option-sell orders

  const result = (Number(price) * qty) / marginVal;

  return result;
};

export const calcMinimumQuantity = ({
  order_config,
  exchange,
  orderSide,
  product,
  price,
  instrumentName = "",
  isSquareOff = false,
  raw_board_lot_quantity = 1,
  marginConfig = null,
  symbol = "",
  instrumentDetails = null,
  defaultEquityConfig = undefined,
}) => {
  // If this is a square-off operation, bypass minimum quantity validation
  if (isSquareOff) return 0;

  if (!price || price <= 0) return 0;

  // Check if dynamic margin config applies
  if (marginConfig && symbol) {
    const dynamicMultiplier = getMarginMultiplierFromConfig({
      marginConfig,
      exchange,
      symbol,
      productType: product,
      instrumentName,
      instrumentDetails,
    });

    if (dynamicMultiplier !== null) {
      // Use dynamic margin for minimum quantity calculation
      const dynamicMarginPerUnit = price / dynamicMultiplier;
      const standardMinOrderValue = 450; // Standard minimum order value
      const minQty = Math.ceil(standardMinOrderValue / dynamicMarginPerUnit);

      return minQty > 0 ? minQty : 1;
    }
  }

  // SPECIAL CASE: For Option SELL orders - handle all exchanges the same way
  if (orderSide === "SELL" && isOptionsInstrument(instrumentName)) {
    const standardMinOrderValue = 450;
    const marginType = getMarginType(product);
    const exchangeConfig = order_config[exchange];

    let config = null;
    let marginPerContract = 0;

    // Get config based on exchange
    if (isCashEquityExchange(exchange)) {
      config = exchangeConfig[orderSide]?.[marginType.toLowerCase()];
    } else {
      // NFO, BFO, MCX, or other derivative exchanges
      config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
    }

    if (config && config.absolute > 0) {
      // Mirror calcMargin: for MCX OPTFUT, divide by (raw_board_lot_quantity * tradingUnitFactor)
      const tradingUnitFactor = (exchange === "MCX" && instrumentDetails?.instrument_name === "OPTFUT")
        ? parseFloat(instrumentDetails?.trading_unit_factor || 0)
        : 0;
      const finalTradingUnitFactor = tradingUnitFactor > 0 ? tradingUnitFactor : 1;

      // marginPerContract now matches what calcMargin uses per lot
      marginPerContract = config.absolute / (raw_board_lot_quantity * finalTradingUnitFactor);

      // Calculate minimum quantity using standardMinOrderValue (450)
      const minQty = Math.ceil(standardMinOrderValue / marginPerContract);

      return minQty;
    }

    // Fallback if config not found
    return 1;
  }

  const marginType = getMarginType(product);
  const exchangeConfig = order_config[exchange];

  if (!exchangeConfig) return 0;

  let config = null;

  // Get the appropriate configuration
  if (isFuturesInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    config = exchangeConfig["FUTURES"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (isOptionsInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (isFuturesInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    config = exchangeConfig[marginType?.toLowerCase()];
  } else if (isOptionsInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    config = exchangeConfig[orderSide]?.[marginType.toLowerCase()];
  } else if (isEquityInstrument(instrumentName) && exchange === "CDS") {
    config = exchangeConfig["EQUITY"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (exchange === "DCSX") {
    config = exchangeConfig["CRYPTO"]?.[orderSide]?.[marginType.toLowerCase()];
  }

  if ((!config || !config.minimumValue) && isCashEquityExchange(exchange) && isEquityInstrument(instrumentName)) {
    config = exchangeConfig["EQUITY"]?.[orderSide]?.[marginType.toLowerCase()];
  }
  if (!config || !config.minimumValue) return 0;

  // Calculate the minimum order value for non-option-sell orders
  let minimumOrderValue;

  if (config.multiplier > 0) {
    minimumOrderValue = config.minimumValue / config.multiplier;
  } else if (config.absolute > 0) {
    // For MCX option sell, always use 500
    if (exchange === "MCX" && orderSide === "SELL" && isOptionsInstrument(instrumentName)) {
      minimumOrderValue = 450;
    } else {
      minimumOrderValue = config.minimumValue / config.absolute;
    }
  } else {
    return 0;
  }

  // Standard margin calculation for non-option-sell orders
  const marginPerUnit = price / (config.multiplier > 0 ? config.multiplier : 1);

  // Calculate minimum quantity based on minimum order value
  const minQty = Math.ceil(minimumOrderValue / marginPerUnit);

  return minQty > 0 ? minQty : 1;
};

// Function to get the minimum order value for display
export const getMinimumOrderValue = ({
  order_config,
  exchange,
  orderSide,
  product,
  instrumentName = "",
  raw_board_lot_quantity = 1,
  marginConfig = null,
  symbol = "",
  instrumentDetails = null,
  defaultEquityConfig = undefined,
}) => {
  // Check if dynamic margin config applies
  if (marginConfig && symbol) {
    const dynamicMultiplier = getMarginMultiplierFromConfig({
      marginConfig,
      exchange,
      symbol,
      productType: product,
      instrumentName,
      instrumentDetails,
    });

    if (dynamicMultiplier !== null) {
      return 450; // Standard minimum order value for dynamic margin
    }
  }

  // Special case for ALL option sell orders - always return 500
  if (orderSide === "SELL" && isOptionsInstrument(instrumentName)) {
    return 450;
  }

  const marginType = getMarginType(product);
  const exchangeConfig = order_config[exchange];

  if (!exchangeConfig) return 0;

  let config = null;

  // Get the appropriate configuration
  if (isFuturesInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    config = exchangeConfig["FUTURES"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (isOptionsInstrument(instrumentName) && !isCashEquityExchange(exchange)) {
    config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (isFuturesInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    config = exchangeConfig[marginType?.toLowerCase()];
  } else if (isOptionsInstrument(instrumentName) && isCashEquityExchange(exchange)) {
    config = exchangeConfig["OPTIONS"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (isEquityInstrument(instrumentName) && exchange === "CDS") {
    config = exchangeConfig["EQUITY"]?.[orderSide]?.[marginType.toLowerCase()];
  } else if (exchange === "DCSX") {
    config = exchangeConfig["CRYPTO"]?.[orderSide]?.[marginType.toLowerCase()];
  }
  else if(isCashEquityExchange(exchange) && isEquityInstrument(instrumentName)) {
    config = exchangeConfig["EQUITY"]?.[orderSide]?.[marginType.toLowerCase()];
  }

  // if ((!config || !config.minimumValue) && exchange === "NSE" && instrumentName?.includes("EQ") && defaultEquityConfig) {
  //   config = defaultEquityConfig;
  // }
  if (!config || !config.minimumValue) return 0;

  // Calculate the minimum order value for other order types
  if (config.multiplier > 0) {
    return config.minimumValue / config.multiplier;
  } else if (config.absolute > 0) {
    return 450;
  } else {
    return 0;
  }
};

// Lowest required margin an order may have when margin comes from the margin API.
const MIN_REQUIRED_MARGIN = 450;

// Reads a numeric API field. A missing value (null, undefined, "") gives NaN, not 0 as Number() would.
const parseApiNumber = (value) =>
  value === null || value === undefined || value === "" ? NaN : Number(value);

// Minimum quantity from a margin API response: the quantity at which required margin reaches
// MIN_REQUIRED_MARGIN, using the response's own margin per unit (required_margin / quantity).
// Returns 0 when the API needs no margin, or null when the response has no usable numbers.
export const calcMinimumQuantityFromMarginResponse = (marginData) => {
  const quantity = parseApiNumber(marginData?.quantity);
  const requiredMargin = parseApiNumber(marginData?.required_margin);

  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(requiredMargin) || requiredMargin < 0) return null;
  if (requiredMargin === 0) return 0;

  return Math.ceil((MIN_REQUIRED_MARGIN * quantity) / requiredMargin);
};
